const loginPage = Object.freeze({
  headerText1: 'How money',
  description: 'Using Emerge on web',
  footerText: `\u00A9 ${new Date().getFullYear()} Emerge, All rights reserved`,
  qrCodeLoginText: 'Scan with your Emerge app to ',
  emergeWebsite: 'Emerge website',
  termsAndConditions: 'Terms and Conditions',
  privacyPolicy: 'Privacy policy'
});

const homePage = Object.freeze({
  xeroButton: 'Connect to XERO',
  logout: 'Log out'
});

const sideNav = Object.freeze({
  home: 'Home',
  accounts: 'Accounts',
  addFavoriteAccounts: 'Add favorite accounts',
  manage: 'Manage',
  settings: 'Settings',
  generalLedgerSettings: 'General Ledger settings',
  xeroConnections: 'Xero connections',
  passkeys: 'Passkeys',
  payments: 'Payments',
  paymentsApprovalRequiredLabel: (count: number) =>
    `${count} payment${count === 1 ? '' : 's'} requiring approval`,
  teamMembers: 'Team members',
  cards: 'Cards',
  help: 'Help',
  findAnswers: 'Find answers fast',
  talkToTeams: 'Talk to our teams',
  logout: 'Logout'
});

const v3HomePage = Object.freeze({
  title: 'Home',
  comingSoon: 'V3 Home page coming soon'
});

const batchPaymentsPage = Object.freeze({
  title: 'Payments',
  // One shared copy for every profile - no batch-specific wording, so no Personal-profile
  // variant is needed (see manage/README.md's Permissions section for the history here).
  subtitle: 'All your scheduled and pending payments and transfers in one spot.',
  createButton: 'Create batch payment',
  tabs: {
    scheduled: 'Scheduled',
    actionRequired: 'Action required',
    batchHistory: 'Batch history'
  },
  columns: {
    createdDate: 'Created date',
    to: 'To/Name',
    type: 'Type',
    scheduledDate: 'Scheduled date',
    amount: 'Amount',
    details: 'Details',
    status: 'Status'
  },
  detailsFromPrefix: 'From:',
  types: {
    xero: 'Xero Batch',
    emerge: 'Batch',
    scheduled: 'Scheduled',
    recurringScheduled: 'Recurring scheduled',
    scheduledTransfer: 'Scheduled transfer'
  },
  status: {
    approved: 'Approved',
    rejected: 'Rejected',
    scheduled: 'Scheduled',
    pending_approval: 'Approval required',
    in_progress: 'In progress',
    completed: 'Completed',
    completed_with_failures: 'Completed with failures',
    failed: 'Failed',
    expired: 'Expired'
  },
  // Shown instead of status.pending_approval when the current user has no authorisation slot on
  // the multi-sign action gating the payment - they have nothing to action, so "Approval
  // required" would be misleading.
  noApprovalAuthorityStatus: 'Pending',
  awaitingAdditionalApprovalStatusLabel: 'Additional approval required',
  emptyState: {
    scheduled: 'No payments are currently scheduled.',
    action_required: 'There are currently no pending actions required.',
    batch_history: 'No batches have been completed.'
  }
});

const scheduledPaymentModal = Object.freeze({
  title: 'Scheduled payment',
  closeLabel: 'Close',
  payeeDetails: 'Payee details',
  paymentDetails: 'Payment details',
  createdByLabel: 'Created by:',
  fromLabel: 'From:',
  statementDetailsLabel: 'Statement details:',
  loadingStatement: 'Loading statement details',
  deleteButton: 'Delete scheduled payment',
  deletingButton: 'Deleting...',
  deleteErrorBanner:
    'Something went wrong deleting this payment. Please contact Support for assistance.',
  deleteConfirmTitle: 'Delete scheduled payment',
  deleteConfirmBody: 'Are you sure you want to delete this scheduled payment?',
  // Shared with BatchOverviewModal's delete confirmation - see DeleteConfirmModal.
  confirmDeleteButton: 'Yes, delete',
  // "Pay now" is this modal's name for retrying a failed individual payment; a failed batch group
  // (see batchModal/batchOverviewModal) calls the same action "Retry payments" instead.
  payNowButton: 'Pay now',
  retryingButton: 'Retrying...',
  retryErrorBanner:
    'Something went wrong retrying this payment. Please contact Support for assistance.',
  rejectedTitle: 'Scheduled payment rejected',
  rejectedBodyLine1: 'You have rejected this payment.',
  rejectedBodyLine2: 'The creator of this payment will be notified along with your reason.',
  closeButton: 'Close',
  deleteBlockedTitle: 'Unable to delete scheduled payment',
  deleteBlockedBody:
    "This payment can't be deleted at this stage because it's within the processing window and may be sent to the bank.",
  // Shown instead of deleteBlockedBody when the delete request itself was rejected (see
  // UnableToDeleteReasonEnum.DELETE_CONFLICT) rather than the pre-emptive client-side window
  // check above - the payment's state already changed server-side by the time it was submitted.
  deleteConflictBody: "This payment is currently being processed and can't be deleted.",
  rejectButton: 'Reject',
  approveButton: 'Approve',
  approvedByLabel: 'Approved by:',
  rejectedByLabel: 'Rejected by:',
  awaitingApprovalFromLabel: 'Awaiting approval from:',
  loadingApprovers: 'Loading approvers',
  awaitingAdditionalApprovalStatusLabel: 'Additional approval required',
  approvedBannerTitle: 'You have approved this payment',
  approvedBannerBody: 'The payment will be processed once all approval requirements are met.',
  approvingLabel: 'Approving...',
  approveErrorBanner:
    "It's not you, it's us. Please try again, and if you're still stuck, let us know, via 'Help & Information' in your menu",
  // Despite the name, this is shown when OTHER approvers are still required after this
  // authorisation (see PaymentOutcomeResult's paymentApproved variant / hasApprovedLocally) - kept
  // as-is rather than renamed to avoid an unrelated diff, but don't be misled by "Final" here.
  approvedFinalTitle: 'Scheduled payment approved',
  approvedFinalBodyLine1: 'You have authorised this payment.',
  approvedFinalBodyLine2: 'The payment will be processed once all approval requirements are met.',
  // Shown when THIS approval was the last one required - no further sign-off needed, the payment
  // is fully scheduled to run on its scheduled date (see PaymentOutcomeResult's paymentScheduled
  // variant / hasFinalApproval).
  finalApprovalTitle: 'Payment scheduled',
  finalApprovalBodyLine1: 'You have authorised this payment.',
  finalApprovalBodyLine2:
    'The creator of this payment will be notified that the payment is now scheduled.',
  // Shown instead of approvedFinal* when this approval was also the final one required AND the
  // immediate same-day payment run it triggered then failed - the multi-sign action itself still
  // completed, only the payment run failed (see MultiSignErrorCodeEnum.EXECUTION_FAILED and
  // useScheduledPaymentAction's handling of it).
  //
  // CAUTION: EXECUTION_FAILED (4104) is a GENERIC "the payment run failed" code, not specifically
  // insufficient funds - the backend distinguishes the real cause only internally (for its own
  // notification templates), then collapses every cause into this one code before it reaches the
  // frontend. This body's "insufficient funds" wording is therefore an assumption (the most common
  // real-world cause of a same-day payment failing, and what the original Figma for this screen
  // specified), not something the error code itself confirms - unlike batchModal's deliberately
  // generic copy, which avoids naming a cause for exactly this reason. Revisit if the backend ever
  // exposes the specific reason to this endpoint.
  paymentFailedTitle: 'Payment failed',
  paymentFailedBody: (accountName: string) =>
    `Your payment has failed due to insufficient funds in "${accountName}". Please transfer funds and approve again.`,
  // Shown when Pay now (retry()) itself succeeds - a retry always runs synchronously, so success
  // here always means "complete", never "scheduled" (see PaymentOutcomeResult's paymentComplete
  // variant / useScheduledPaymentAction's hasRetried). Names the real source account (same
  // payFromAccount as paymentFailedBody above), not a generic message.
  paymentCompleteTitle: 'Payment complete',
  paymentCompleteBody: (accountName: string) =>
    `Your payment from "${accountName}" is complete. This will show in your transaction history.`,
  doneButton: 'Done',
  emptyValue: '-',
  unknownName: '-'
});

const scheduledTransferModal = Object.freeze({
  title: 'Scheduled transfer',
  closeLabel: 'Close',
  fromLabel: 'From:',
  toLabel: 'To:',
  noteLabel: 'Note',
  // Shown for a FAILED transfer, next to the failure banner - hinting that retrying (Transfer
  // now) sends it immediately rather than waiting for the next scheduled run.
  asapPillLabel: 'Your Transfer will go out ASAP',
  deleteButton: 'Delete scheduled transfer',
  deletingButton: 'Deleting...',
  deleteErrorBanner:
    'Something went wrong deleting this transfer. Please contact Support for assistance.',
  deleteConfirmTitle: 'Delete scheduled transfer',
  deleteConfirmBody: 'Are you sure you want to delete this scheduled transfer?',
  confirmDeleteButton: 'Yes, delete',
  transferNowButton: 'Transfer now',
  transferringButton: 'Transferring...',
  retryErrorBanner:
    'Something went wrong retrying this transfer. Please contact Support for assistance.',
  // A wallet-to-wallet transfer has no payee to fail a CoP/payee check on, so a failed attempt is
  // reasonably always down to the source account's own balance - unlike scheduledPaymentModal's
  // deliberately generic EXECUTION_FAILED copy, which covers a wider (and less certain) set of
  // causes.
  failedStatusLabel: 'Transfer failed',
  failedBannerTitle: 'Failed - Insufficient funds',
  failedBannerBody: 'This transfer failed due to the account having insufficient funds.',
  // Full-screen takeover shown instead of the generic retryErrorBanner above when Transfer now
  // itself comes back with PaymentErrorCodeEnum.INSUFFICIENT_FUNDS (4017), confirming the cause.
  // Mirrors scheduledPaymentModal's paymentFailed outcome (see PaymentOutcomeResult's
  // transferFailed variant / useScheduledPaymentAction's hasImmediateFailure).
  transferFailedTitle: 'Transfer failed',
  transferFailedBody: (accountName: string) =>
    `Your transfer has failed due to insufficient funds in "${accountName}". Please transfer funds and try again.`,
  // Shown when Transfer now (retry()) itself succeeds - a retry always runs synchronously, so
  // success here always means "complete" (see PaymentOutcomeResult's transferComplete variant /
  // useScheduledPaymentAction's hasRetried). Names the real source account (same payFromAccount
  // as transferFailedBody above), not a generic message.
  transferCompleteTitle: 'Transfer complete',
  transferCompleteBody: (accountName: string) =>
    `Your transfer from "${accountName}" is complete. This will show in your transaction history.`,
  deleteBlockedTitle: 'Unable to delete scheduled transfer',
  deleteBlockedBody:
    "This transfer can't be deleted at this stage because it's within the processing window and may be sent to the bank.",
  deleteConflictBody: "This transfer is currently being processed and can't be deleted.",
  closeButton: 'Close',
  emptyValue: '-'
});

// Failure copy is deliberately generic rather than naming a cause (e.g. "insufficient funds") -
// the backend doesn't expose a failure reason, and asserting one we can't actually confirm would
// be misleading.
const batchModal = Object.freeze({
  title: 'Batch',
  totalAmountLabel: 'Total batch amount',
  batchDetailsLabel: 'Batch details',
  viewPaymentsButton: 'View payments',
  viewPaymentsToApproveButton: 'View payments to approve',
  retryPaymentsButton: 'Retry payments',
  // EXPIRED's disabled second button, next to View payments - "Retry" would wrongly imply a
  // payment attempt failed, when an expired batch never went out in the first place.
  payNowButton: 'Pay now',
  processedButIncompleteLabel: 'Processed but incomplete',
  markedAsCompleteLabel: 'Marked as complete',
  expiredBannerTitle: 'Expired',
  expiredBannerBody:
    "The batch wasn't processed because it wasn't approved before the scheduled payment date. Once approved, you can still pay it manually.",
  partialFailureBannerTitle: (failedCount: number) => `${failedCount} Payments failed`,
  partialFailureBannerBody:
    "Some payments in this batch couldn't be completed. Please view your payments to see more.",
  allFailureBannerTitle: 'All payments failed',
  allFailureBannerBody: 'None of the payments in this batch could be completed.',
  // The one exception to the generic copy above: shown instead of allFailureBanner* when the
  // pay-from account's live balance can't cover the batch total right now. Inferred, since the
  // backend keeps no insufficient-funds state for a batch. Title reuses
  // partialFailureBannerTitle's "{X} Payments failed".
  loadingBanner: 'Loading batch status',
  insufficientFundsBannerBody:
    'Payments within this batch has failed due to insufficient funds.\nPlease view your payments to see more.'
});

const batchOverviewModal = Object.freeze({
  backLinkLabel: 'Batch Overview',
  loadingOutcome: 'Loading batch outcome',
  title: 'Batch payment',
  batchNameLabel: 'Batch name:',
  batchTotalLabel: 'Batch total:',
  payFromLabel: 'Pay from:',
  scheduledForLabel: 'Scheduled for:',
  createdByLabel: 'Batch created by:',
  approvedByLabel: 'Batch approved by:',
  rejectedByLabel: 'Batch rejected by:',
  reasonLabel: 'Reason:',
  deleteBatchButton: 'Delete batch',
  deleteConfirmTitle: 'Delete batch payment',
  deleteConfirmBody: (name: string) =>
    `Are you sure you wish to delete batch "${name}".\n\nDeleting this batch will result in none of the payments being processed and a notification sent to all account admins.`,
  unableToDeleteTitle: 'Unable to delete batch',
  deleteConflictBody: "This batch is currently in progress and can't be deleted.",
  // Shown instead of deleteConflictBody when Delete is clicked while within the daily
  // delete-block window (see isWithinDeleteBlockWindow) - the API was never called for this one,
  // unlike deleteConflictBody's server-side conflict.
  deleteBlockedBody:
    'This batch can’t be deleted at this stage because its scheduled payment is within the processing window and may be sent to the bank.',
  viewBatchButton: 'View batch',
  markAsCompleteButton: 'Mark as complete',
  markingAsCompleteButton: 'Marking as complete...',
  markAsCompleteErrorBanner:
    'Something went wrong marking this batch as complete. Please contact Support for assistance.',
  retryErrorBanner:
    'Something went wrong retrying these payments. Please contact Support for assistance.',
  markAsCompleteConfirmTitle: 'Mark as complete',
  markAsCompleteConfirmBody:
    "Marking this batch as complete will move it to Batch History. Any failed payments won't be processed, and no further action can be taken.",
  paidAmountSuffix: (paidAmount: string) => ` (${paidAmount} paid)`,
  approvalRequiredStatus: 'Batch payment approval required',
  // Same "no authority to act on this" case as batchPaymentsPage.noApprovalAuthorityStatus,
  // worded to match this modal's other "Batch payment ..." status copy.
  noApprovalAuthorityStatus: 'Batch pending approval',
  approvedStatus: 'Batch payment approved',
  // Shown instead of approvedStatus for an APPROVED batch with no multiSignActionId - it was
  // never actually approved by anyone, just scheduled to go out (see isScheduledWithoutMultiSign).
  scheduledStatus: 'Batch payment scheduled',
  inProgressStatus: 'Batch payment in progress',
  completedStatus: 'Batch payment completed',
  // Shown instead of incompleteStatus when every payment in the batch failed (see
  // isBatchAllFailed) - a genuine partial failure keeps the "processed but incomplete" copy.
  failedStatus: 'Batch payment failed',
  incompleteStatus: 'Batch payment processed but incomplete',
  markedAsCompleteStatus: 'Batch payment marked as complete',
  rejectedStatus: 'Batch payment rejected',
  expiredStatus: 'Batch payment expired',
  paymentsSectionLabel: 'Payments',
  columns: {
    companyPayeeName: 'Company/Payee name',
    bankAccountNumber: 'Bank account number',
    amount: 'Amount',
    particulars: 'Particulars',
    code: 'Code',
    reference: 'Reference',
    paymentDate: 'Payment date'
  },
  paymentsCountLabel: (count: number) => `${count} Payments`,
  // Full-screen takeovers shown in place of the modal's own content - final approval, rejection,
  // and (while the modal is open live) an in-progress batch resolving to either outcome.
  batchOutcome: {
    // Shown when other approvers are still required after this authorisation (see
    // PaymentOutcomeResult's batchApproved variant / hasApprovedLocally) - mirrors
    // scheduledPaymentModal's approvedFinalTitle/BodyLine* for the individual-payment flow.
    approvedTitle: 'Batch approved',
    approvedBodyLine1: 'You have authorised this batch payment.',
    approvedBodyLine2: 'The batch will be processed once all approval requirements are met.',
    scheduledTitle: 'Batch scheduled',
    scheduledBodyLine1: 'Batch scheduled to be paid.',
    scheduledBodyLine2: 'Each payment will show in your transactions.',
    completeTitle: 'Batch complete',
    completeBody: 'These payments will show in your transaction history.',
    failedTitle: 'Batch failed',
    failedBody: 'Your batch has failed. Please try again.',
    rejectedTitle: 'Batch rejected',
    rejectedBodyLine1: (batchName: string) => `You have rejected "${batchName}" batch payment.`,
    rejectedBodyLine2: 'The creator of this payment will be notified along with your reason.'
  }
});

/**
 * Shared by ScheduledPaymentModal and BatchOverviewModal for the 4100/4103 authorisation-conflict
 * flow (see reconcileAfterAuthorisationConflict in useScheduledPaymentAction) - explains what
 * actually happened before the modal closes back to the list, rather than leaving the user to
 * guess why Approve/Reject just vanished. `subject` is interpolated with either
 * paymentSubject ("This payment") or batchSubject ("This batch payment").
 */
const alreadyActionedModal = Object.freeze({
  gotItButton: 'Got it',
  paymentSubject: 'This payment',
  batchSubject: 'This batch payment',
  approvedTitle: 'Already approved',
  approvedBody: (subject: string) =>
    `${subject} has already received all required approvals and will be processed as scheduled.`,
  // The action is fully approved, and it was this customer's own authorisation (from another
  // device, e.g. the mobile app) that did it.
  approvedByYouTitle: 'Already approved',
  approvedByYouBody: (subject: string) =>
    `You already approved ${subject.toLowerCase()} from another device. No further action is needed - it will be processed as scheduled.`,
  // This customer approved from another device, but the rule still needs other signers - the
  // action itself is still pending, just not on this customer's account anymore.
  approvedPendingByYouTitle: 'Approval recorded',
  approvedPendingByYouBody: (subject: string) =>
    `You already approved ${subject.toLowerCase()} from another device. It's still awaiting approval from other signers before it's processed.`,
  rejectedTitle: 'Already rejected',
  rejectedBody: (subject: string) =>
    `${subject} has already been rejected by another approver. No further action is needed.`,
  rejectedByYouTitle: 'Already rejected',
  rejectedByYouBody: (subject: string) =>
    `You already rejected ${subject.toLowerCase()} from another device. No further action is needed.`,
  expiredTitle: 'Approval window expired',
  expiredBody: (subject: string) =>
    `${subject} has expired and can no longer be approved or rejected.`,
  // Fallback for a conflict whose outcome doesn't cleanly map to any of the above (belt-and-
  // suspenders - every real MultiSignActionState is covered by a case above).
  genericTitle: 'Status updated',
  genericBody: (subject: string) =>
    `${subject}'s status has changed since this screen last checked. The latest status is now shown.`
});

// Shared by every screen that renders a "Particulars, Code, Reference" statement line.
const statementDetails = Object.freeze({
  noParticulars: 'No particulars',
  noCode: 'No code',
  noReference: 'No reference'
});

const rejectPaymentModal = Object.freeze({
  title: 'Reject payment',
  description:
    'Please provide a reason for rejecting this payment. Your reason will be recorded in the Account Rule Log.',
  batchTitle: 'Reject batch',
  batchDescription:
    'Please provide a reason for rejecting this batch. Your reason will be recorded in the Account Rule Log.',
  reasonLabel: 'Reason',
  confirmButton: 'Yes, reject',
  confirmingButton: 'Rejecting...',
  cancelButton: 'Cancel',
  submitError:
    "It's not you, it's us. Please try again, and if you're still stuck, let us know, via 'Help & Information' in your menu"
});

const addFavoriteAccountsModal = Object.freeze({
  title: 'Favorite accounts',
  description: 'Select the accounts you would like to pin to your sidebar.',
  searchPlaceholder: 'Search by account name or number',
  noAccountsAvailable: "You don't have any accounts yet.",
  noSearchResults: 'No accounts match that search.',
  applyButton: 'Apply',
  closeButton: 'Close',
  columns: {
    account: 'Account',
    balance: 'Balance'
  },
  itemLabel: 'Accounts'
});

const v3GeneralLedgerSettingsPage = Object.freeze({
  title: 'General Ledger Settings',
  comingSoon: 'V3 General Ledger Settings page coming soon'
});

const v3AccountDetailPage = Object.freeze({
  title: 'Account',
  comingSoon: 'V3 Account detail page coming soon'
});

const idleTimeoutModal = Object.freeze({
  title: 'Still there?',
  bodyPrefix: "You'll be logged out in ",
  remainingLabel: (secondsRemaining: number) => `${secondsRemaining} seconds`,
  bodySuffix: '.',
  stayActiveHint: 'Move your mouse to stay signed in.'
});

const batchCreationInvalidAutoMappingModal = Object.freeze({
  title: 'Invalid field format',
  body: 'There was an issue with your file.\nPlease review the formatting and try again.',
  okButton: 'OK'
});

const transferMoneyPage = Object.freeze({
  formTitle: 'Transfer funds',
  reviewTitle: 'Review transfer',
  fromLabel: 'From',
  toLabel: 'To',
  amountLabel: 'Amount',
  noteLabel: 'Note',
  scheduleLabel: 'Schedule',
  insufficientFundsError: 'Insufficient funds',
  scheduleInsufficientFundsWarning:
    'Warning: You can schedule the transfer, but there are currently insufficient funds for it to be processed.',
  reviewButton: 'Review',
  editButton: 'Edit',
  transferButton: 'Transfer',
  scheduleTransferButton: 'Schedule transfer',
  transferDetailsLabel: 'Transfer details',
  fromRowLabel: 'From:',
  toRowLabel: 'To:',
  goOutAsapPill: 'Your transfer will go out ASAP.',
  scheduler: {
    repeatLabel: 'Repeat',
    startDateLabel: 'Start date',
    todayAsap: 'Today (ASAP)',
    periodOptions: {
      never: 'Never',
      weekly: 'Weekly',
      fortnightly: 'Fortnightly',
      everyFourWeeks: 'Every four weeks',
      everyMonth: 'Every month'
    }
  },
  success: {
    title: 'Transfer complete',
    description: 'This will show in your transaction history.'
  },
  scheduledSuccess: {
    title: 'Transfer scheduled',
    description:
      'Your transfer can be edited and reviewed until date of transfer. It will show in your transaction history.'
  },
  failed: {
    title: 'An error occurred while preparing this transfer.',
    description: 'Please try again.'
  },
  makeNewTransferButton: 'Make a new transfer'
});

const passkeySettingsPage = Object.freeze({
  title: 'Passkeys',
  signInHeading: 'Sign in with passkeys.',
  description:
    'Use your fingerprint, face, screen lock or security key to log in to your Emerge account.',
  securityNote:
    'Passkeys are a safe way to sign in. Keep your device locks private and your security keys secure so only you can access your account.',
  createButton: 'Create passkey',
  listTitle: 'Passkeys',
  lastUsedPrefix: 'Last used:',
  lastUsedNever: 'Never used',
  emptyState: {
    title: 'Create a Passkey',
    description: 'You don’t have any passkeys yet. Add one for a faster, more secure way to log in.'
  },
  removeConfirm: {
    title: 'Remove passkey?',
    description:
      'Removing this passkey will stop it being used to sign in to your account. You can add a new passkey at any time.',
    confirmButton: 'Remove passkey',
    cancelButton: 'Cancel'
  },
  errors: {
    createFailed:
      'An error has occurred creating your passkey. Please contact support if the issue persists.',
    createCancelled: 'Passkey creation was cancelled.',
    loadFailed:
      'An error has occurred loading your passkeys. Please contact support if the issue persists.',
    removeFailed:
      'An error has occurred removing the passkey. Please contact support if the issue persists.',
    unsupported: 'Passkeys are not supported on this browser or device.'
  }
});

const teamMembersPage = Object.freeze({
  title: 'Team Members',
  subtitle:
    'Manage who has access to this account. Click a row to view contact details and issued cards.',
  searchPlaceholder: 'Search by name, phone or email',
  emptyState: 'No team members match that search.',
  loadError: 'We couldn’t load your team members. Please try again.',
  columns: {
    name: 'Name',
    phoneNumber: 'Phone number',
    email: 'Email',
    accessLevel: 'Access level',
    cards: 'Cards'
  },
  accessLevel: {
    owner: 'Owner',
    admin: 'Admin',
    team_member: 'Team member',
    none: 'No access'
  },
  accountStatus: {
    pending: 'Pending',
    paused: 'Paused'
  },
  itemLabel: 'Team members',
  selectionBar: {
    freezeCards: 'Freeze cards',
    unfreezeCards: 'Unfreeze cards',
    updateAccess: 'Update access',
    pause: 'Pause',
    enableAccess: 'Enable access',
    remove: 'Remove',
    cancel: 'Cancel'
  },
  confirmModals: {
    freezeCards: {
      title: 'Freeze cards',
      titleSingular: 'Freeze card',
      body: 'Freezing these cards will stop new purchases. You can unfreeze it at any time.',
      bodySingular: 'Freezing this card will stop new purchases. You can unfreeze it at any time.',
      cta: 'Freeze',
      ctaSubmitting: 'Freezing...'
    },
    unfreezeCards: {
      title: 'Unfreeze cards',
      titleSingular: 'Unfreeze card',
      body: 'Unfreezing these cards will allow new purchases.',
      bodySingular: 'Unfreezing this card will allow new purchases.',
      cta: 'Unfreeze',
      ctaSubmitting: 'Unfreezing...'
    },
    pauseMembers: {
      title: 'Pause team members',
      titleSingular: 'Pause team member',
      // Body already reads fine for one person ("their"/"associated cards" both work whether
      // it's one team member or several), so it's shared - only the title needs to singularize.
      body: 'This will pause their access to the account and freeze all associated cards.',
      cta: 'Pause',
      ctaSubmitting: 'Pausing...'
    },
    enableMembers: {
      title: 'Enable these team members?',
      titleSingular: 'Enable this team member?',
      // Same reasoning as pauseMembers - "They'll"/"they have a card" already reads fine singular.
      body: 'They’ll be able to spend once their cards are unfrozen or they have a card.',
      cta: 'Enable',
      ctaSubmitting: 'Enabling...'
    },
    removeMembers: {
      title: 'Remove team members',
      titleSingular: 'Remove team member',
      body: 'Are you sure you would like to remove these team members? This will delete all cards associated these people.',
      bodySingular:
        'Are you sure you would like to remove this team member? This will delete all cards associated this person.',
      cta: 'Remove',
      ctaSubmitting: 'Removing...'
    },
    cancel: 'Cancel'
  },
  updateAccessLevelModal: {
    title: 'Update access level',
    options: {
      team_member: {
        label: 'Team member',
        description: 'Access to assigned cards only'
      },
      admin: {
        label: 'Admin',
        description: 'Full access and financial controls'
      }
    },
    cta: 'Update',
    ctaSubmitting: 'Updating...'
  },
  errorModal: {
    freezeCards: 'Unable to freeze cards',
    freezeCardsSingular: 'Unable to freeze card',
    unfreezeCards: 'Unable to unfreeze cards',
    unfreezeCardsSingular: 'Unable to unfreeze card',
    pauseMembers: 'Unable to pause team members',
    pauseMembersSingular: 'Unable to pause team member',
    enableMembers: 'Unable to enable team members',
    enableMembersSingular: 'Unable to enable team member',
    removeMembers: 'Unable to remove team members',
    removeMembersSingular: 'Unable to remove team member',
    updateAccessLevel: 'Unable to update access level',
    close: 'Close'
  },
  detailsDrawer: {
    title: 'Team member',
    cardsHeading: 'Cards',
    createCard: 'Create card',
    noCardsTitle: 'Create a card for this team member',
    noCardsSubtitle: 'Tap the "+ Create card" above and follow the steps.',
    frozenSuffix: 'Frozen',
    cardLimitLabel: (amount: string) => `Limit $${amount}`,
    settingsAction: 'Settings',
    freezeAction: 'Freeze',
    unfreezeAction: 'Unfreeze',
    deleteAction: 'Delete'
  },
  createCardModal: {
    title: 'Create card',
    labelField: 'Card label',
    labelPlaceholder: 'Label',
    walletField: 'Linked account',
    walletPlaceholder: 'Select account',
    typeField: 'Card type',
    types: {
      virtual: {
        label: 'Virtual',
        description: 'Use immediately online and with Apple Pay.'
      },
      physical: {
        label: 'Physical',
        description: 'Also works immediately as a virtual card. Additional cost may apply.'
      },
      subscription: {
        label: 'Subscription',
        description: 'Ideal for SaaS products, subscriptions and online services.'
      },
      agentic: {
        label: 'Agentic',
        description: 'Secure virtual cards for AI agents, with tight limits built in.'
      }
    },
    noWallet: 'No account is available to create a card against.',
    cancel: 'Cancel',
    cta: 'Create',
    ctaSubmitting: 'Creating...',
    errorTitle: 'Unable to create card',
    // Purely a client-side quick-fill convenience - EmergeFrontService has no card-label-
    // suggestion or category concept at all (confirmed against BatchCreateCardsUseCase/
    // CreateCardUseCase and RqCreateCard - "name" is a free-text field with no enum behind it).
    // One set per CardTypeEnum value - keyed directly by it, see CreateCardModal.
    labelSuggestions: {
      virtual: ['General', 'Expenses', 'Supplies'],
      physical: ['General', 'Expenses', 'Primary'],
      subscription: ['Saas', 'Subscriptions', 'Online'],
      agentic: ['Agentic', 'Agent', 'AI']
    }
  },
  inviteButton: 'Invite team member',
  inviteModal: {
    title: 'Invite team member',
    editTitle: 'Update invitation',
    firstNameField: 'First name',
    firstNamePlaceholder: 'First name',
    lastNameField: 'Last name',
    lastNamePlaceholder: 'Last name',
    phoneField: 'Mobile number',
    phoneInvalid: 'Enter a valid mobile number.',
    emailField: 'Email address',
    emailPlaceholder: 'email@email.com',
    emailInvalid: 'Enter a valid email address.',
    accessField: 'Account access',
    legalPrefix: 'I have permission to contact this individual and have read and agreed to the',
    privacyPolicy: 'Privacy Policy',
    legalConjunction: 'and',
    termsAndConditions: 'Terms and Conditions',
    cta: 'Send invitation',
    ctaSubmitting: 'Sending...',
    editCta: 'Update invitation',
    editCtaSubmitting: 'Updating...',
    deleteInvite: 'Delete invite',
    errorTitle: 'Unable to send invitation'
  },
  deleteInviteModal: {
    title: 'Delete invitation',
    body: 'Are you sure you want to delete the invitation?',
    cta: 'Delete',
    ctaSubmitting: 'Deleting...'
  },
  inviteSuccessModal: {
    title: 'Email sent',
    body: 'We’ve sent an invite to {email}',
    inviteAnother: 'Invite another team member'
  },
  deleteCardModal: {
    title: 'Delete card',
    body: 'Are you sure you want to delete this card?',
    cta: 'Delete',
    ctaSubmitting: 'Deleting...'
  },
  cardSettingsModal: {
    title: 'Card settings',
    limitsSectionTitle: 'Set spend limits for this card',
    limitsSectionDescription:
      'Choose recurring limits that reset automatically, or one total amount.',
    recurringTitle: 'Recurring spend limits',
    recurringSubtitle: 'Periodic limits that reset automatically',
    daily: { label: 'Daily', subtitle: 'Resets daily' },
    weekly: { label: 'Weekly', subtitle: 'Resets weekly' },
    monthly: { label: 'Monthly', subtitle: 'Resets monthly' },
    totalTitle: 'Total spend limit',
    totalSubtitle: 'Like a gift card. Use until the amount is reached',
    totalAmountLabel: 'Amount',
    totalNoResetLabel: 'No reset',
    permissionsSectionTitle: 'Spend permissions',
    permissionsSectionDescription: 'Which transactions are allowed on this card?',
    permissions: {
      online: { label: 'Online', subtitle: 'Purchases online' },
      international_pos: {
        label: 'International in-store',
        subtitle: 'In-person purchases overseas'
      },
      atm: { label: 'ATM withdrawals', subtitle: 'Cash withdrawals from an ATM' },
      contactless: { label: 'Contactless', subtitle: 'Contactless with card or Apple/Google Pay' }
    },
    permissionsFooterNote: 'Anything switched off will be declined when being paid.',
    cta: 'Confirm',
    ctaSubmitting: 'Saving...',
    errorTitle: 'Unable to update card settings'
  }
});

const passkeySetupPromptModal = Object.freeze({
  title: 'Log in with passkey',
  description:
    'You can now log in with a passkey instead of a QR code. Sign in with Face ID, Touch ID, or your device lock - it’s faster, easier, and just as secure.',
  createButton: 'Create passkey',
  notNowButton: 'Not now'
});

const cardsPage = Object.freeze({
  title: 'Cards',
  subtitle:
    'Virtual and physical cards issued to your team. Each card draws from a linked account and follows its own limits and controls.',
  searchPlaceholder: 'Search by Team member, card label or suffix',
  createCardButton: 'Create card',
  itemLabel: 'Cards',
  emptyState: 'No cards to display.',
  loadError: 'Something went wrong loading your cards. Please try again.',
  youBadge: 'You',
  columns: {
    member: 'Team member',
    card: 'Card',
    type: 'Type',
    status: 'Status',
    limits: 'Limits',
    linkedAccount: 'Linked account'
  },
  cardType: {
    physical: 'Physical',
    virtual: 'Virtual',
    subscription: 'Subscription',
    agentic: 'Agentic'
  },
  status: {
    active: 'Active',
    frozen: 'Frozen'
  },
  limitType: {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    total: 'Total'
  },
  selectionBar: {
    freezeCards: 'Freeze cards',
    unfreezeCards: 'Unfreeze cards',
    cancel: 'Cancel'
  },
  selectMemberModal: {
    title: 'Select a team member',
    searchPlaceholder: 'Search team members'
  }
});

export {
  loginPage,
  homePage,
  sideNav,
  v3HomePage,
  batchPaymentsPage,
  scheduledPaymentModal,
  scheduledTransferModal,
  batchModal,
  batchOverviewModal,
  alreadyActionedModal,
  statementDetails,
  rejectPaymentModal,
  v3AccountDetailPage,
  v3GeneralLedgerSettingsPage,
  addFavoriteAccountsModal,
  idleTimeoutModal,
  batchCreationInvalidAutoMappingModal,
  transferMoneyPage,
  passkeySettingsPage,
  passkeySetupPromptModal,
  teamMembersPage,
  cardsPage
};
